import {Subscription} from "rxjs";
import {DatePipe} from "@angular/common";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {Component, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Params, Router, RouterLink} from "@angular/router";

import {ToastrService} from "ngx-toastr";
import {NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {NgOptionTemplateDirective, NgSelectComponent} from "@ng-select/ng-select";

import {
  defaultPageLimit,
  ImageComponent,
  PaginationComponent,
  SearchFilterComponent,
  SearchInputComponent,
  SearchResultComponent,
  SortOption
} from "@numizmat/components";
import {
  AdCategoryApiService,
  AdCategoryStateService, AdSubcategoryApiService, AdSubcategoryStateService,
  ProfileAdSortOptionApiService,
  ProfileAdSortOptionStateService
} from "@numizmat/services/nomenclature";
import {appRoutes} from "@numizmat/constants";
import {environment} from "@numizmat/environments";
import {AlertService} from "@numizmat/services/common";
import {AdStatus, AdType} from "@numizmat/enumerations";
import {RestoreScrollPositionDirective} from "@numizmat/directives";
import {AdRenewalIndividualModalComponent} from "@numizmat/modals";
import {AdApiService, ProfileAdApiService} from "@numizmat/services/core";
import {AdCategoryResponse, AdSubcategoryResponse} from "@numizmat/services-models/nomenclature";
import {
  FilterProfileAdResponse,
  FilterProfileAdsRequest,
  FilterProfileAdsResponse
} from "@numizmat/services-models/core";
import {
  AdRenewalBatchModalComponent
} from "@numizmat/pages/profile-page/components/own-profile/modals/ad-renewal-batch-modal/ad-renewal-batch-modal.component";

@Component({
  selector: 'nz-profile-ads',
  standalone: true,
  imports: [
    DatePipe,
    RouterLink,
    ImageComponent,
    PaginationComponent,
    SearchInputComponent,
    SearchFilterComponent,
    SearchResultComponent,
    RestoreScrollPositionDirective,
    FormsModule,
    ReactiveFormsModule,
    NgOptionTemplateDirective,
    NgSelectComponent
  ],
  templateUrl: './profile-ads.component.html',
  styleUrl: './profile-ads.component.scss'
})
export class ProfileAdsComponent implements OnInit, OnDestroy {
  @Input() targetMenuId!: string;

  private router: Router = inject(Router);
  private modalService: NgbModal = inject(NgbModal);
  private adApiService: AdApiService = inject(AdApiService);
  private alertService: AlertService = inject(AlertService);
  private toastrService: ToastrService = inject(ToastrService);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private profileAdApiService: ProfileAdApiService = inject(ProfileAdApiService);
  private profileAdSortOptionApiService: ProfileAdSortOptionApiService = inject(ProfileAdSortOptionApiService);
  private profileAdSortOptionStateService: ProfileAdSortOptionStateService = inject(ProfileAdSortOptionStateService);

  private adCategoryApiService: AdCategoryApiService = inject(AdCategoryApiService);
  private adCategoryStateService: AdCategoryStateService = inject(AdCategoryStateService);
  private adSubcategoryApiService: AdSubcategoryApiService = inject(AdSubcategoryApiService);
  private adSubcategoryStateService: AdSubcategoryStateService = inject(AdSubcategoryStateService);

  private currentRoute: string = '';
  private subs: Subscription = new Subscription();

  protected loading: boolean = true;
  protected totalResults: number = 0;
  protected searchValue: string = '';
  protected selectedPage: number = 1;
  protected sortOptions: SortOption[] = [];
  protected selectedSortOption: number = 1;
  protected selectedAdType: number = 0;
  protected selectedAdStatus: number = 0;
  protected activeFiltersCount: number = 0;
  protected selectedAdCategoryId: number = 0;
  protected selectedAdSubcategoryId: number = 0;
  protected selectedPageLimitOptionId: number = defaultPageLimit;
  protected ads: FilterProfileAdResponse[] = [];
  protected adCategories: AdCategoryResponse[] = [];
  protected adSubcategories: AdSubcategoryResponse[] = [];
  protected filteredAdSubcategories: AdSubcategoryResponse [] = [];

  protected readonly AdType = AdType;
  protected readonly AdStatus = AdStatus;
  protected readonly appRoutes = appRoutes;
  protected readonly environment = environment;
  protected readonly defaultPageLimit = defaultPageLimit;

  public ngOnInit(): void {
    this.currentRoute = this.router.url.split('?')[0];
    this.getAllAdCategories();
    this.getAllAdSubcategories();
    this.getAllProfileAdSortOptions();
    this.createQueryParamsChangeSubscription();
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  protected onOpenAdRenewalBatchModal(): void {
    const modalRef: NgbModalRef = this.modalService.open(AdRenewalBatchModalComponent, {
      size: 'lg',
      scrollable: true,
      modalDialogClass: 'full-height-modal'
    });
    const subscription: Subscription = modalRef.componentInstance.reloadData.subscribe({
      next: (success: boolean) => {
        if (success) {
          this.filterProfileAds();
        }
      }
    });
    this.subs.add(subscription);
  }

  protected onOpenAdRenewalIndividualModal(ad: FilterProfileAdResponse, mode: 'after-sold' | 'after-expired'): void {
    const modalRef: NgbModalRef = this.modalService.open(AdRenewalIndividualModalComponent, {
      scrollable: true,
      modalDialogClass: 'full-height-modal'
    });
    modalRef.componentInstance.mode = mode;
    modalRef.componentInstance.adId = ad.adId;
    modalRef.componentInstance.adTitle = ad.adTitle;
    const subscription: Subscription = modalRef.componentInstance.reloadData.subscribe({
      next: (success: boolean) => {
        if (success) {
          this.filterProfileAds();
        }
      }
    });
    this.subs.add(subscription);
  }

  protected onSort(sortOption: number): void {
    this.router.navigate([this.currentRoute], {
      queryParamsHandling: 'merge',
      queryParams:
        {sortOption: sortOption, page: 1},
    }).then();
  }

  protected onSearch(searchValue: string): void {
    this.router.navigate([this.currentRoute], {
      queryParamsHandling: 'merge',
      queryParams:
        {searchValue: searchValue, page: 1},
    }).then();
  }

  protected onChangeAdStatus(event: any): void {
    const currentAdStatus = Number(event.target.value);
    if (currentAdStatus === this.selectedAdStatus) {
      return;
    }
    this.router.navigate([this.currentRoute], {
      queryParamsHandling: 'merge',
      queryParams:
        {adStatus: currentAdStatus, page: 1},
    }).then();
  }

  protected onChangeAdType(event: any): void {
    const currentAdType = Number(event.target.value);
    if (currentAdType === this.selectedAdType) {
      return;
    }
    this.router.navigate([this.currentRoute], {
      queryParamsHandling: 'merge',
      queryParams:
        {adType: currentAdType, page: 1},
    }).then();
  }

  protected onChangeAdCategoryId(adCategoryId: number): void {
    adCategoryId = adCategoryId === null ? 0 : adCategoryId;
    if (this.selectedAdCategoryId === adCategoryId) {
      return;
    }
    this.selectedAdCategoryId = adCategoryId;
    this.selectedAdSubcategoryId = 0;
    this.filterAdSubcategories();
    this.router.navigate([this.currentRoute], {
      queryParamsHandling: 'merge',
      queryParams:
        {adCategoryId: this.selectedAdCategoryId, adSubcategoryId: 0, page: 1},
    }).then();
  }

  protected onChangeAdSubcategoryId(adSubcategoryId: number): void {
    adSubcategoryId = adSubcategoryId === null ? 0 : adSubcategoryId;
    if (this.selectedAdSubcategoryId === adSubcategoryId) {
      return;
    }
    this.selectedAdSubcategoryId = adSubcategoryId;
    this.router.navigate([this.currentRoute], {
      queryParamsHandling: 'merge',
      queryParams:
        {adSubcategoryId: this.selectedAdSubcategoryId, page: 1},
    }).then();
  }

  protected clearFilters(): void {
    this.router.navigate([this.currentRoute], {
      queryParamsHandling: 'merge',
      queryParams:
        {adStatus: 0, adType: 0, adCategoryId: 0, adSubcategoryId: 0, page: 1},
    }).then();
  }

  protected showStopAdWarning(ad: FilterProfileAdResponse): void {
    this.alertService.showStopAdWarning(ad.adTitle).then((isConfirmed) => {
      if (isConfirmed) {
        this.loading = true;
        this.adApiService.stopAd(ad.adId).subscribe({
          next: response => {
            this.loading = false;
            if (response.success) {
              this.totalResults = 0;
              this.filterProfileAds();
              this.toastrService.success(response.successMessage);
            } else {
              this.toastrService.error(response.errorMessage);
            }
          }
        });
      }
    });
  }

  protected showDeleteAdWarning(ad: FilterProfileAdResponse): void {
    this.alertService.showDeleteAdWarning(ad.adTitle).then((isConfirmed) => {
      if (isConfirmed) {
        this.loading = true;
        this.adApiService.deleteAd(ad.adId).subscribe({
          next: response => {
            this.loading = false;
            if (response.success) {
              this.totalResults = 0;
              this.filterProfileAds();
              this.toastrService.success(response.successMessage);
            } else {
              this.toastrService.error(response.errorMessage);
            }
          }
        })
      }
    });
  }

  private createQueryParamsChangeSubscription(): void {
    const subscription: Subscription = this.activatedRoute.queryParams.subscribe({
      next: (queryParams: Params) => {
        const menuId = queryParams['menuId'];
        if (menuId !== this.targetMenuId) {
          return;
        }
        this.searchValue = queryParams['searchValue'];
        this.selectedPage = !!queryParams['page'] ? Number.parseInt(queryParams['page']) : 1;
        this.selectedPageLimitOptionId = !!queryParams['pageLimit'] ? Number.parseInt(queryParams['pageLimit']) : defaultPageLimit;
        this.selectedSortOption = !!queryParams['sortOption'] ? Number.parseInt(queryParams['sortOption']) : 1;
        this.selectedAdStatus = !!queryParams['adStatus'] ? Number.parseInt(queryParams['adStatus']) : 0;
        this.selectedAdType = !!queryParams['adType'] ? Number.parseInt(queryParams['adType']) : 0;
        this.selectedAdCategoryId = !!queryParams['adCategoryId'] ? Number.parseInt(queryParams['adCategoryId']) : 0;
        this.selectedAdSubcategoryId = !!queryParams['adSubcategoryId'] ? Number.parseInt(queryParams['adSubcategoryId']) : 0;

        this.filterAdSubcategories();
        this.getActiveFiltersCount();
        this.filterProfileAds();
      }
    });
    this.subs.add(subscription);
  }

  private filterProfileAds(): void {
    this.loading = true;
    const request: FilterProfileAdsRequest = {
      searchValue: this.searchValue,
      adCategoryId: this.selectedAdCategoryId,
      adSubcategoryId: this.selectedAdSubcategoryId,
      adStatus: this.selectedAdStatus,
      adType: this.selectedAdType,
      selectedPage: this.selectedPage,
      selectedPageLimit: this.selectedPageLimitOptionId,
      selectedSortOption: this.selectedSortOption,
    };
    this.profileAdApiService.filterProfileAds(request).subscribe({
      next: (response: FilterProfileAdsResponse) => {
        this.totalResults = response.totalResults;
        this.ads = response.ads;
        this.loading = false;
      }
    });
  }

  private getAllAdCategories(): void {
    const subscription: Subscription = this.adCategoryStateService.getAllAdCategories().subscribe({
      next: adCategories => {
        if (!!adCategories) {
          this.adCategories = [...adCategories];
          this.adCategories.unshift({
            adCategoryId: 0,
            adCategoryName: 'Всички',
            adCategoryDescription: ''
          });
        } else {
          this.adCategoryApiService.getAllAdCategories().subscribe({
            next: response => {
              this.adCategoryStateService.setAllAdCategories(response);
            }
          });
        }
      }
    });
    this.subs.add(subscription);
  }

  private getAllAdSubcategories(): void {
    const subscription: Subscription = this.adSubcategoryStateService.getAllAdSubcategories().subscribe({
      next: adSubcategories => {
        if (!!adSubcategories) {
          this.adSubcategories = adSubcategories;
        } else {
          this.adSubcategoryApiService.getAllAdSubcategories().subscribe({
            next: response => {
              this.adSubcategoryStateService.setAllAdSubcategories(response);
            }
          });
        }
      }
    });
    this.subs.add(subscription);
  }

  private filterAdSubcategories(): void {
    if (!!this.selectedAdCategoryId && !!this.adSubcategories) {
      this.filteredAdSubcategories = this.adSubcategories.filter(x => x.adCategoryId === this.selectedAdCategoryId);
    }
    this.filteredAdSubcategories.unshift({
      adCategoryId: this.selectedAdCategoryId,
      adSubcategoryId: 0,
      adSubcategoryName: 'Всички',
      sortOrder: 0
    });
  }

  private getAllProfileAdSortOptions(): void {
    const subscription: Subscription = this.profileAdSortOptionStateService.getAllProfileAdSortOptions().subscribe({
      next: adSortOptions => {
        if (!!adSortOptions) {
          this.sortOptions = adSortOptions.map(x => {
            return {
              id: x.profileAdSortOption,
              name: x.profileAdSortOptionName
            }
          });
        } else {
          this.profileAdSortOptionApiService.getAllProfileAdSortOptions().subscribe({
            next: response => {
              this.profileAdSortOptionStateService.setAllProfileAdSortOptions(response);
            }
          })
        }
      }
    });
    this.subs.add(subscription);
  }

  private getActiveFiltersCount(): void {
    let currentActiveFiltersCount = 0;

    if (this.selectedAdCategoryId !== 0) {
      currentActiveFiltersCount++;
    }
    if (this.selectedAdSubcategoryId !== 0) {
      currentActiveFiltersCount++;
    }
    if (this.selectedAdStatus !== 0) {
      currentActiveFiltersCount++;
    }
    if (this.selectedAdType !== 0) {
      currentActiveFiltersCount++;
    }

    this.activeFiltersCount = currentActiveFiltersCount;
  }
}
